import { useIsDesktop } from "common/MediaQueries"
import { gsap } from "gsap"
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin"
import { ScrollTrigger } from "gsap/dist/ScrollTrigger"
import useWindowSize from "hooks/useWindowSize"
import { RefObject, useCallback, useEffect, useRef, useState } from "react"
import FloatingText from "reusable/mintStory/FloatingText"
import FloatingTextContainer from "reusable/mintStory/FloatingTextContainer"
import styled, { createGlobalStyle } from "styled-components"
import { dropRender, wrapImageUrlWithNextImageParams } from "./helpers"
import { DropFrame, TextDataProps } from "./types"

const StyledContainer = styled.div`
  position: relative;
  height: 100vh;
  transform: translate(0px, 0px);
`

const StyledCanvas = styled.canvas``
const StyledFadeFromBlack = styled.div<{ $show: boolean }>`
  position: absolute;
  inset: 0;
  background: black;
  transition: opacity 1.5s ease-in;
  opacity: ${(p) => (p.$show ? 1 : 0)};
`
const StyledWhiteFade = styled.div.attrs({ className: "white-fade" })<{
  $show: boolean
}>`
  position: fixed;
  inset: 0;
  background: ${(p) => p.theme.colors.lightGrey};
  z-index: 1;
  visibility: ${(p) => (p.$show ? "visible" : "hidden")};
`
const GlobalStyle = createGlobalStyle`
  /* :root {
    height:100%;
  }

  body{
    height:100%;
  }

  #__next {
    height:100%;
  } */
  .pin-spacer{
    // transition to next section positioning
    margin-bottom:-85vh !important;
    ${(p) => p.theme.breakpoints.up("md")} {
      margin-bottom:-89vh !important;
  }
    /* z-index: 1 !important; */
    mix-blend-mode: darken;
  }
  ::-webkit-scrollbar-thumb {
    background-color:  ${(p) => p.theme.colors.darkGrey};
    border-radius:16px;
  }
`

type StoryProps = {
  //text data overlay
  textData: TextDataProps[]
  //path for mobile images
  mobilePath: string
  //Path for desktop images
  desktopPath: string
  //Headers for mobile + desktop in array
  headerSize: number[]
  //Frames count
  framesCount: number

  //Extra padding to make story longer
  extraPadding: number
  //Callback when story is almost complete
  onAlmostComplete?: () => void
  //frame from where gsap becomes faster
  speedUpFrom?: number
  scrollBtnRef?: RefObject<HTMLButtonElement>
}

//include gsap and scrolltrigger
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)

// ScrollTrigger.config({ ignoreMobileResize: true })
// ScrollTrigger.normalizeScroll(true)

function MintStory({
  textData,
  mobilePath,
  desktopPath,
  framesCount,
  headerSize,
  extraPadding,
  onAlmostComplete,
  speedUpFrom,
  scrollBtnRef,
}: StoryProps) {
  const innerScrollBtnRef = scrollBtnRef.current

  //State for canvas to be rendered
  const [count, setCount] = useState(0)
  //State for image loading progress
  const [progress, setProgress] = useState(0)
  // state for loading
  const [loading, setLoading] = useState(true)

  //Canvas Ref
  const ref = useRef<HTMLCanvasElement>(null)
  //Container Ref
  const containerRef = useRef<HTMLDivElement>(null)

  const { width, height } = useWindowSize()

  const isDesktop = useIsDesktop()

  // use effect that set the loading to false when the images are loaded
  useEffect(() => {
    if (progress === 100) {
      setLoading(false)
    }
  }, [progress])

  const currentDropFrame = useCallback(
    (index: number) =>
      width < 768
        ? wrapImageUrlWithNextImageParams(
            `${mobilePath}${index.toString().padStart(3, "0")}.webp`,
            856
          )
        : wrapImageUrlWithNextImageParams(
            `${desktopPath}${index.toString().padStart(3, "0")}.webp`,
            1920
          ),
    [desktopPath, mobilePath, width]
  )

  const initiateGsap = useCallback(() => {
    // if components loaded and got dom width & height for canvas to be responsive
    if (ref.current && width && height) {
      const dropContext = ref.current.getContext("2d")
      // Set the size of the canvas
      //  plus 2 to handle the scroll bar in smasung
      ref.current.width = width + 2
      // plus 10 to handle the screen size change
      ref.current.height = height + 10

      // Count the frames and set the path to the images
      const dropFrameCount: number = framesCount

      // Set start frame
      const dropImages: HTMLImageElement[] = []
      const drop: DropFrame = {
        dropFrame: 0,
      }
      let loadedImageCount = 0
      // Go through frames from start to finish
      for (let i = 0; i < dropFrameCount; i++) {
        const dropImg = new Image()
        // add style to canvas image
        // image to be cover
        dropImg.style.objectFit = "cover"
        dropImg.src = currentDropFrame(i)
        dropImages.push(dropImg)
        // if image is loaded add to loadedImageCount
        if (dropImg.complete) {
          loadedImageCount++
          setProgress((loadedImageCount / dropFrameCount) * 100)
        } else {
          dropImg.addEventListener("load", () => {
            loadedImageCount++
            // set progress to be used in loading bar
            setProgress((loadedImageCount / dropFrameCount) * 100)
          })
        }
      }

      let scrollTimeout

      // Function to add class
      const addScrollingClass = () => {
        innerScrollBtnRef.classList.add("hide-text")
      }

      // Function to remove class
      const removeScrollingClass = () => {
        innerScrollBtnRef.classList.remove("hide-text")
      }

      // Timeline of gsap
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          //padding for menu
          start: () =>
            width < 768 ? `top: ${headerSize[0]}px` : `top: ${headerSize[1]}px`,
          //length for the video/scroll extra padding on bottom
          end: () => `+=${extraPadding}`,
          toggleActions: "restart none none reverse",
          //this makes frames to stick on scroll
          scrub: 0,
          //pin makes video pinned in container
          pin: true,
          //markers when animation starts
          markers: false,
          //auto padding no need for static height
          pinSpacing: true,
          invalidateOnRefresh: true,
          fastScrollEnd: false,
          onUpdate: () => {
            clearTimeout(scrollTimeout)
            addScrollingClass() // Remove class when scroll begins

            // Set a timeout to add the class if no scroll for 0.5 seconds
            scrollTimeout = setTimeout(() => {
              removeScrollingClass()
            }, 500) // 0.5 seconds timeout until text is shown
          },
        },
      })
      // add animation to canvas
      // add zIndex to pin-spacer to make it on top
      timeline.add("pre").set(".pin-spacer", { zIndex: "1" }, "pre")

      // start animation

      timeline
        .add("start")
        .to(
          drop,
          {
            dropFrame: dropFrameCount - 1,
            snap: "dropFrame",
            duration: (progress) => {
              // Check if the current progress is after the speedup prop
              const needsToSpeedUp = speedUpFrom
                ? progress > speedUpFrom
                : false
              // If it is within range, apply a shorter duration, else keep the existing duration (30)
              return needsToSpeedUp ? 1 : 30
            },
            // use animation onUpdate instead of scrollTrigger's onUpdate
            onUpdate: () => {
              dropRender(dropContext, ref, dropImages, drop, setCount)
              if (drop.dropFrame > 412) {
                innerScrollBtnRef.classList.add("invert")
              } else {
                innerScrollBtnRef.classList.remove("invert")
              }
            },
          },
          "start"
        )
        .to(
          innerScrollBtnRef,
          {
            left: isDesktop ? "20px" : "5px",
            transform: "translateX(0)",
            autoAlpha: 1,
            duration: 1,
            onStart: () => {
              innerScrollBtnRef.classList.remove("show-text")
            },
            onComplete: () => {
              innerScrollBtnRef.classList.add("show-text")
            },
            onReverseComplete: () => {
              innerScrollBtnRef.classList.remove("show-text")
            },
          },
          "0.1"
        )
        .to(innerScrollBtnRef, { duration: 1, toggleClass: "show-text" })

      // add scale and opacity to canvas when the animation ends
      timeline.add("end").to(ref.current, { scale: 0.6, duration: 1 }, "-=1")

      timeline.add("done").set(
        ".pin-spacer",
        {
          autoAlpha: 0,
          duration: 0.3,
          onReverseComplete: onAlmostComplete,
        },
        "-=0.2"
      )
      // remove zIndex from pin-spacer to make it below the next section

      timeline.add("done").set(".pin-spacer", { zIndex: "auto" }, "-=0")

      timeline
        .add("done")
        .to(
          ".white-fade",
          {
            autoAlpha: 0,
            duration: 0.2,
            onReverseComplete: onAlmostComplete,
          },
          "-=0.2"
        )
        .to(innerScrollBtnRef, { autoAlpha: 0, duration: 0.2 })
      dropImages[0].onload = () =>
        dropRender(dropContext, ref, dropImages, drop, setCount)
    }
  }, [currentDropFrame, extraPadding, framesCount, headerSize, height, width])

  useEffect(() => {
    if (width) {
      setTimeout(() => {
        initiateGsap()
      }, 0)
    }
    ScrollTrigger.refresh()

    return () => {
      ScrollTrigger.killAll()
    }
  }, [width])

  useEffect(() => {
    if (height && ref.current) {
      setTimeout(() => {
        console.log(height)
        ref.current.height = height
      }, 0)
    }
  }, [height])

  //use effect that enable and disable scroll trigger by the loading state
  useEffect(() => {
    if (loading) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [loading])

  return (
    <>
      <GlobalStyle />
      <StyledWhiteFade $show={!loading} />
      <StyledContainer ref={containerRef}>
        <StyledCanvas ref={ref}></StyledCanvas>
        {!count && <StyledFadeFromBlack $show={loading} />}
        {textData.map(
          (
            { containerProps: { height, width, top, bottom }, children },
            containerIndex
          ) => {
            return (
              <FloatingTextContainer
                key={containerIndex}
                height={height}
                width={width}
                top={top}
                bottom={bottom}
              >
                {children.map(
                  (
                    { text, variant, start, end, middle, alwaysShow },
                    childIndex
                  ) => {
                    return (
                      <FloatingText
                        key={childIndex}
                        text={text}
                        count={count}
                        variant={variant}
                        start={start}
                        end={end}
                        middle={middle}
                        alwaysShow={alwaysShow}
                        progress={progress}
                      />
                    )
                  }
                )}
              </FloatingTextContainer>
            )
          }
        )}
      </StyledContainer>
    </>
  )
}

export default MintStory
